import { BadgeCheck, BookText, Lightbulb, Bookmark, BookmarkCheck, CircleCheck, CircleX, Flag, Languages } from "lucide-react";
import { Link } from "react-router-dom";
import { EXAM_BY_ID } from "../data/exams";
import type { Question } from "../data/types";
import { isCorrect } from "../lib/scoring";
import { toggleBookmark } from "../lib/progress";
import { progressStore, useProgress } from "../lib/store";
import { Inline } from "./RichText";
import { DifficultyChip } from "./ui";
import { explanationFor } from "../data/localized";
import { useLang, useT } from "../i18n";

const KEYS = ["A", "B", "C", "D", "E", "F"];
const NUMBER_WORDS: Record<number, string> = { 2: "two", 3: "three", 4: "four" };

export function domainName(q: Question): string {
  return EXAM_BY_ID[q.exam].domains.find((d) => d.id === q.domain)?.name ?? q.domain;
}

interface Props {
  question: Question;
  /** order[vị trí hiển thị] = chỉ số gốc. */
  order: number[];
  selected: number[];
  onChange?: (next: number[]) => void;
  revealed: boolean;
  showMeta?: boolean;
  number?: number;
}

export function QuestionView({ question: q, order, selected, onChange, revealed, showMeta = true, number }: Props) {
  const progress = useProgress();
  const t = useT();
  const lang = useLang();
  const multi = q.answer.length > 1;
  const locked = revealed || !onChange;
  const bookmarked = progress.bookmarks.includes(q.id);

  const toggle = (orig: number) => {
    if (locked) return;
    if (multi) {
      const has = selected.includes(orig);
      onChange?.(has ? selected.filter((x) => x !== orig) : [...selected, orig]);
    } else {
      onChange?.([orig]);
    }
  };

  const correct = revealed && isCorrect(selected, q.answer);
  const exam = EXAM_BY_ID[q.exam];

  return (
    <article aria-labelledby={`q-${q.id}`}>
      {showMeta && (
        <div className="q-meta">
          <span className="chip" style={{ color: exam.accent }}>{exam.code}</span>
          <span className="chip">{domainName(q)}</span>
          <DifficultyChip level={q.difficulty} />
          {q.official && (
            <span className="chip official" title={t("Câu mẫu trích nguyên văn từ exam guide chính thức của Anthropic", "Sample question quoted from Anthropic's official exam guide")}>
              <BadgeCheck className="ico" size={14} aria-hidden="true" /> {t("Câu mẫu chính thức", "Official sample")}
            </span>
          )}
          <button
            type="button"
            className="btn ghost sm"
            style={{ marginLeft: "auto" }}
            aria-pressed={bookmarked}
            onClick={() => progressStore.update((p) => toggleBookmark(p, q.id))}
            title={bookmarked ? t("Bỏ đánh dấu", "Remove bookmark") : t("Đánh dấu để ôn lại", "Bookmark for review")}
          >
            {bookmarked ? <><BookmarkCheck className="ico" size={16} aria-hidden="true" /> {t("Đã đánh dấu", "Bookmarked")}</> : <><Bookmark className="ico" size={16} aria-hidden="true" /> {t("Đánh dấu", "Bookmark")}</>}
          </button>
        </div>
      )}
      <div className="q-prompt" id={`q-${q.id}`} lang="en">
        {number !== undefined && <span className="muted">Question {number}. </span>}
        <Inline text={q.prompt} />
        {multi && <strong className="select-n"> (Select {NUMBER_WORDS[q.answer.length] ?? q.answer.length}.)</strong>}
      </div>
      {q.code && <pre lang="en"><code>{q.code}</code></pre>}

      <div className="options" role={multi ? "group" : "radiogroup"} aria-labelledby={`q-${q.id}`}>
        {order.map((orig, pos) => {
          const isSel = selected.includes(orig);
          const isAns = q.answer.includes(orig);
          let cls = "option";
          if (revealed) {
            if (isAns) cls += " correct";
            else if (isSel) cls += " incorrect";
          } else if (isSel) cls += " selected";
          if (locked) cls += " locked";
          return (
            <button
              type="button"
              key={orig}
              className={cls}
              role={multi ? "checkbox" : "radio"}
              aria-checked={isSel}
              aria-disabled={locked}
              onClick={() => toggle(orig)}
            >
              <span className="key" aria-hidden="true">{KEYS[pos]}</span>
              <span lang="en">
                <Inline text={q.options[orig] ?? ""} />
                {revealed && isAns && <span className="sr-only"> ({t("đáp án đúng", "correct answer")})</span>}
                {revealed && isSel && !isAns && <span className="sr-only"> ({t("bạn chọn — sai", "your choice — incorrect")})</span>}
              </span>
            </button>
          );
        })}
      </div>

      {revealed && (
        <div className="explain" aria-live="polite">
          <div className={`callout ${correct ? "ok" : "bad"}`}>
            <strong className="row-inline">{correct ? <><CircleCheck className="ico" size={18} aria-hidden="true" /> {t("Chính xác!", "Correct!")}</> : selected.length ? <><CircleX className="ico" size={18} aria-hidden="true" /> {t("Chưa đúng.", "Not quite.")}</> : t("Bạn chưa trả lời câu này.", "You didn't answer this question.")}</strong>{" "}
            {t("Đáp án:", "Answer:")} {q.answer.map((a) => KEYS[order.indexOf(a)]).sort().join(", ")}
          </div>
          <p lang={lang}><Inline text={explanationFor(q, lang)} /></p>
          {q.notes && (
            <div className="option-notes">
              <h4 className="small mb-0">{t("Vì sao từng phương án", "Why each option")}</h4>
              <ul>
                {order.map((orig, pos) => {
                  const ok = q.answer.includes(orig);
                  const note = (lang === "en" ? q.notes?.en : q.notes?.vi)?.[orig];
                  if (!note) return null;
                  return (
                    <li key={orig} className={ok ? "ok" : "bad"}>
                      <span className="key" aria-hidden="true">{KEYS[pos]}</span>
                      {ok ? <CircleCheck className="ico" size={16} aria-label={t("đúng", "correct")} /> : <CircleX className="ico" size={16} aria-label={t("sai", "incorrect")} />}
                      <span lang={lang}><Inline text={note} /></span>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
          {q.takeaway && (
            <p className="callout takeaway" lang={lang}>
              <Lightbulb className="ico" size={16} aria-hidden="true" /> <strong>{t("Ghi nhớ:", "Remember:")}</strong>{" "}
              <Inline text={lang === "en" ? q.takeaway.en : q.takeaway.vi} />
            </p>
          )}
          {q.vi && lang === "vi" && (
            <details className="vi-version">
              <summary><Languages className="ico" size={16} aria-hidden="true" /> Xem đề bằng tiếng Việt</summary>
              <p className="mb-0"><Inline text={q.vi.prompt} /></p>
              <ol className="vi-options">
                {order.map((orig, pos) => (
                  <li key={orig} value={pos + 1} data-key={KEYS[pos]}>
                    <strong>{KEYS[pos]}.</strong> <Inline text={q.vi?.options[orig] ?? ""} />
                  </li>
                ))}
              </ol>
            </details>
          )}
          {q.sources?.length ? (
            <div className="small sources">
              <BookText className="ico" size={16} aria-hidden="true" /> <strong>{t("Nguồn:", "Sources:")}</strong>
              <ul>
                {q.sources.map((src) => (
                  <li key={src.url}><a href={src.url} target="_blank" rel="noreferrer noopener">{src.title}</a></li>
                ))}
              </ul>
            </div>
          ) : q.ref && (
            <p className="small">
              <BookText className="ico" size={16} aria-hidden="true" /> {t("Tài liệu:", "Reference:")}{" "}
              <a href={q.ref} target="_blank" rel="noreferrer noopener">{q.ref.includes(".pdf") || q.ref.includes("+Exam+Guide") ? t("Exam guide chính thức (PDF)", "Official exam guide (PDF)") : q.ref.replace(/^https?:\/\//, "")}</a>
            </p>
          )}
          <p className="tiny muted mb-0">
            <Link to={`/feedback?q=${encodeURIComponent(q.id)}&kind=question`}>
              <Flag className="ico" size={14} aria-hidden="true" /> {t("Báo lỗi câu hỏi này", "Report an issue with this question")}
            </Link>
          </p>
        </div>
      )}
    </article>
  );
}

export { KEYS as OPTION_KEYS };
